import axios from 'axios';
import type {
  LoginCredentials,
  RegisterData,
  RegisterRequestData,
  User,
  UserRole,
  AuditLogEntry,
  ActivityLogEntry,
  AdminNotification,
  UserNotification,
  TrainingSlot,
  EventSeries,
  CancellationInfo,
  Booking,
  DefaultAssignment,
  MembershipType,
  TrainingTokenInfo,
  Task,
  TaskComment,
  WaitlistEntry,
  Idea,
  IdeaComment,
  AssignmentRequest,
} from '../types';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const API_BASE = API_URL.replace('/api', '') || 'http://localhost:3002';

/**
 * Resolve a file URL — returns absolute URLs as-is, prefixes relative URLs with API_BASE.
 */
export function resolveFileUrl(url: string | null | undefined): string {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${API_BASE}${url}`;
}

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token and tenant slug to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const tenantSlug = localStorage.getItem('tenantSlug');
  if (tenantSlug) {
    config.headers['X-Tenant-Slug'] = tenantSlug;
  }
  return config;
});

// Auth API
export const authAPI = {
  login: async (credentials: LoginCredentials) => {
    const response = await api.post<{ token: string; user: User }>('/auth/login', credentials);
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
    }
    return response.data;
  },

  // Admin-created user registration (legacy, used by UsersTab)
  register: async (data: RegisterData) => {
    const response = await api.post<{ token: string; user: User }>('/auth/register', data);
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
    }
    return response.data;
  },

  // Self-registration with email verification flow
  registerRequest: async (data: RegisterRequestData) => {
    const response = await api.post<{ message: string }>('/auth/register-request', data);
    return response.data;
  },

  verifyEmail: async (token: string) => {
    const response = await api.get<{ message: string }>(`/auth/verify-email?token=${token}`);
    return response.data;
  },

  forgotPassword: async (email: string) => {
    const response = await api.post<{ message: string }>('/auth/forgot-password', { email });
    return response.data;
  },

  resetPassword: async (token: string, password: string) => {
    const response = await api.post<{ message: string }>('/auth/reset-password', { token, password });
    return response.data;
  },

  getMe: async () => {
    const response = await api.get<User>('/auth/me');
    return response.data;
  },

  updateMe: async (data: {
    name?: string; surname?: string; preferredLanguage?: string;
    bike?: string; dressSize?: string; phone?: string; photoUrl?: string;
    riderLevel?: string; averagePace?: string; groupRidingExperience?: boolean;
    bikeType?: string;
  }) => {
    const response = await api.patch<User>('/auth/me', data);
    return response.data;
  },

  uploadProfilePhoto: async (file: File) => {
    const formData = new FormData();
    formData.append('photo', file);
    const response = await api.post<{ id: string; photoUrl: string }>('/auth/me/photo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  changePassword: async (currentPassword: string, newPassword: string) => {
    const response = await api.post<{ message: string }>('/auth/change-password', { currentPassword, newPassword });
    return response.data;
  },

  checkEmail: async (email: string) => {
    const response = await api.post<{ requiresPassword: boolean }>('/auth/check-email', { email });
    return response.data;
  },

  emailLogin: async (email: string, password?: string) => {
    const response = await api.post<{ token: string; user: { id: string; email: string; name: string | null; surname: string | null; role: string; onboardingCompleted: boolean; photoUrl: string | null } }>('/auth/email-login', { email, password });
    return response.data;
  },

  logout: () => {
    localStorage.removeItem('token');
  },

  acceptGdprConsent: async () => {
    const response = await api.post('/auth/gdpr-consent');
    return response.data;
  },

  exportMyData: async () => {
    const response = await api.get('/auth/my-data');
    return response.data;
  },

  deleteMyAccount: async () => {
    const response = await api.delete('/auth/delete-account');
    return response.data;
  },
};

// Admin notifications & audit log API
export const adminAPI = {
  getNotifications: async () => {
    const response = await api.get<AdminNotification[]>('/admin/notifications');
    return response.data;
  },

  markNotificationsRead: async () => {
    await api.patch('/admin/notifications/read');
  },

  getPendingUsers: async () => {
    const response = await api.get<User[]>('/users/pending');
    return response.data;
  },

  approveUser: async (id: string) => {
    const response = await api.post<User>(`/users/${id}/approve`);
    return response.data;
  },

  rejectUser: async (id: string, reason?: string, allowReply?: boolean) => {
    const response = await api.post<User>(`/users/${id}/reject`, { reason, allowReply });
    return response.data;
  },

  getAuditLog: async (page = 1, filters: { action?: string; user?: string; admin?: string; dateFrom?: string; dateTo?: string } = {}, limit?: number) => {
    const params = new URLSearchParams({ page: String(page) });
    if (limit) params.set('limit', String(limit));
    if (filters.action) params.set('action', filters.action);
    if (filters.user) params.set('user', filters.user);
    if (filters.admin) params.set('admin', filters.admin);
    if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.set('dateTo', filters.dateTo);
    const response = await api.get<{ entries: AuditLogEntry[]; total: number; page: number; pageSize: number }>(`/admin/audit-log?${params}`);
    return response.data;
  },

  getActivityLog: async (params: { page?: number; search?: string; method?: string; status?: string; limit?: number } = {}) => {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.set('page', String(params.page));
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.search) searchParams.set('search', params.search);
    if (params.method) searchParams.set('method', params.method);
    if (params.status) searchParams.set('status', params.status);
    const response = await api.get<{ entries: ActivityLogEntry[]; total: number; page: number; pageSize: number }>(
      `/admin/activity-log?${searchParams.toString()}`
    );
    return response.data;
  },
};

// Slots API
export const slotsAPI = {
  getSlots: async (week?: string, all?: boolean) => {
    const params: any = {};
    if (week) {
      params.week = week;
    }
    if (all) {
      params.all = 'true';
    }
    const response = await api.get<TrainingSlot[]>('/slots', { params });
    return response.data;
  },

  getSlot: async (id: string) => {
    const response = await api.get<TrainingSlot>(`/slots/${id}`);
    return response.data;
  },

  createSlot: async (slot: Omit<TrainingSlot, 'id' | 'createdAt' | 'updatedAt' | 'bookingCount' | 'isFull' | 'availableSpots' | 'attendees'>) => {
    const response = await api.post<TrainingSlot | TrainingSlot[]>('/slots', slot);
    return response.data;
  },

  updateSlot: async (id: string, slot: Partial<TrainingSlot>) => {
    const response = await api.put<TrainingSlot & { cancellation?: CancellationInfo }>(`/slots/${id}`, slot);
    return response.data;
  },

  deleteSlot: async (id: string) => {
    const response = await api.delete<{ message: string; cancellation?: CancellationInfo }>(`/slots/${id}`);
    return response.data;
  },

  claimTrainer: async (id: string) => {
    const response = await api.post<{ trainerId: string | null; trainerName: string | null; trainerRideCount: number | null }>(`/slots/${id}/claim-trainer`);
    return response.data;
  },

  getAttendance: async (slotId: string) => {
    const response = await api.get<Array<{ bookingId: string; userId: string; name: string | null; surname: string | null; email: string | null; attended: boolean }>>(`/slots/${slotId}/attendance`);
    return response.data;
  },

  confirmAttendance: async (slotId: string, attendedUserIds: string[]) => {
    const response = await api.post(`/slots/${slotId}/attendance`, { attendedUserIds });
    return response.data;
  },

  bulkCreateSlots: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post<{ created: number; skipped: number; failed: number; total: number; failures: any[] }>(
      '/slots/bulk',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  downloadSlotTemplate: async (format: 'csv' | 'json' = 'csv') => {
    const response = await api.get(`/slots/template?format=${format}`, { responseType: 'blob' });
    const ext = format === 'json' ? 'json' : 'csv';
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `training-events.${ext}`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
};

// Event Files API
export interface EventPhoto {
  id: string;
  url: string;
  trainingSlotId: string;
  uploadedById?: string | null;
  createdAt: string;
}

export const eventFilesAPI = {
  getPhotos: async (slotId: string) => {
    const response = await api.get<EventPhoto[]>(`/slots/${slotId}/photos`);
    return response.data;
  },
  uploadPhotos: async (slotId: string, files: File[]) => {
    const formData = new FormData();
    files.forEach((f) => formData.append('photos', f));
    const response = await api.post<EventPhoto[]>(`/slots/${slotId}/photos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  deletePhoto: async (photoId: string) => {
    await api.delete(`/slots/photos/${photoId}`);
  },
};

// Event Comments API
export interface EventComment {
  id: string;
  text: string;
  createdAt: string;
  userId: string;
  user: { id: string; name?: string | null; surname?: string | null; email?: string | null };
}

export const eventCommentsAPI = {
  getComments: async (slotId: string) => {
    const response = await api.get<EventComment[]>(`/slots/${slotId}/comments`);
    return response.data;
  },
  createComment: async (slotId: string, text: string) => {
    const response = await api.post<EventComment>(`/slots/${slotId}/comments`, { text });
    return response.data;
  },
  updateComment: async (slotId: string, commentId: string, text: string) => {
    const response = await api.patch<EventComment>(`/slots/${slotId}/comments/${commentId}`, { text });
    return response.data;
  },
  deleteComment: async (slotId: string, commentId: string) => {
    await api.delete(`/slots/${slotId}/comments/${commentId}`);
  },
};

// Courses API
export interface Course {
  id: string;
  name: string;
  gpxFileUrl: string | null;
  createdAt: string;
  _count?: { trainingSlots: number };
}

export const coursesAPI = {
  getCourses: async () => {
    const response = await api.get<Course[]>('/courses');
    return response.data;
  },
  createCourse: async (name: string, gpxFile?: File) => {
    const formData = new FormData();
    formData.append('name', name);
    if (gpxFile) formData.append('gpx', gpxFile);
    const response = await api.post<Course>('/courses', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  updateCourse: async (courseId: string, name?: string, gpxFile?: File) => {
    const formData = new FormData();
    if (name) formData.append('name', name);
    if (gpxFile) formData.append('gpx', gpxFile);
    const response = await api.put<Course>(`/courses/${courseId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  deleteCourse: async (courseId: string) => {
    await api.delete(`/courses/${courseId}`);
  },
};

// Bookings API
export const bookingsAPI = {
  getMyBookings: async () => {
    const response = await api.get<Booking[]>('/bookings/my-bookings');
    return response.data;
  },

  getSlotBookings: async (slotId: string) => {
    const response = await api.get<Booking[]>(`/bookings/slot/${slotId}`);
    return response.data;
  },

  createBooking: async (trainingSlotId: string) => {
    const response = await api.post<Booking>('/bookings', { trainingSlotId });
    return response.data;
  },

  updateBooking: async (id: string, userId: string) => {
    const response = await api.put<Booking>(`/bookings/${id}`, { userId });
    return response.data;
  },

  adminCreateBooking: async (trainingSlotId: string, userId: string) => {
    const response = await api.post<Booking>('/bookings/admin', { trainingSlotId, userId });
    return response.data;
  },

  deleteBooking: async (id: string) => {
    await api.delete(`/bookings/${id}`);
  },
};

// Ride History (admin) types
export type RideHistoryWeekStatus = 'ATTENDED' | 'NO_SHOW' | 'NOT_BOOKED' | 'CANCELLED' | 'NO_RIDE';

export interface RideHistoryEvent {
  slotId: string;
  title: string;
  date: string;       // YYYY-MM-DD
  startTime: string;  // HH:mm
  cancelled: boolean;
  booked: boolean;
  attended: boolean;
}

export interface RideHistoryWeek {
  weekStart: string;  // YYYY-MM-DD (Monday)
  status: RideHistoryWeekStatus;
  grantBookingId: string | null;
  events: RideHistoryEvent[];
}

export interface RideHistoryResponse {
  user: {
    id: string;
    name: string | null;
    surname: string | null;
    email: string | null;
    rideCount: number;
    currentStreak: number;
    totalPoints: number;
  };
  weeks: RideHistoryWeek[];
}

// Users API
export const usersAPI = {
  getUsers: async () => {
    const response = await api.get<User[]>('/users');
    return response.data;
  },

  createUser: async (data: { email: string; password: string; name?: string; surname?: string; role?: UserRole }) => {
    const response = await api.post<User>('/users', data);
    return response.data;
  },

  bulkCreateUsers: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post<{
      created: number;
      failed: number;
      stripeSynced: number;
      stripeFailed: number;
      users: (User & {
        password: string;
        stripeSync?: {
          synced: boolean;
          subscriptionId?: string;
          membershipType?: string | null;
          status?: string;
          tokensIssued?: number;
          error?: string;
        } | null;
      })[];
      failures: Array<{ name: string; surname: string; email: string; reason: string }>;
    }>(
      '/users/bulk',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  updateUser: async (id: string, data: { email?: string; password?: string; name?: string; surname?: string; role?: UserRole }) => {
    const response = await api.put<User>(`/users/${id}`, data);
    return response.data;
  },

  deleteUser: async (id: string) => {
    const response = await api.delete<{ message: string }>(`/users/${id}`);
    return response.data;
  },

  downloadTemplate: async () => {
    const response = await api.get('/users/template', {
      responseType: 'blob',
    });
    return response.data;
  },

  getRideHistory: async (userId: string) => {
    const response = await api.get<RideHistoryResponse>(`/users/${userId}/ride-history`);
    return response.data;
  },

  grantAttendance: async (userId: string, bookingId: string) => {
    const response = await api.post<{ message: string }>(`/users/${userId}/grant-attendance`, { bookingId });
    return response.data;
  },
};

// Default Assignments API
export const defaultAssignmentsAPI = {
  getDefaultAssignments: async (slotId?: string, userId?: string) => {
    const params: any = {};
    if (slotId) params.slotId = slotId;
    if (userId) params.userId = userId;
    const response = await api.get<DefaultAssignment[]>('/default-assignments', { params });
    return response.data;
  },

  getSlotDefaultAssignments: async (slotId: string) => {
    const response = await api.get<DefaultAssignment[]>(`/default-assignments/slot/${slotId}`);
    return response.data;
  },

  getUserDefaultAssignments: async (userId: string) => {
    const response = await api.get<DefaultAssignment[]>(`/default-assignments/user/${userId}`);
    return response.data;
  },

  createDefaultAssignment: async (userId: string, trainingSlotId: string) => {
    const response = await api.post<DefaultAssignment>('/default-assignments', {
      userId,
      trainingSlotId,
    });
    return response.data;
  },

  deleteDefaultAssignment: async (id: string) => {
    await api.delete(`/default-assignments/${id}`);
  },

  deleteDefaultAssignmentByUserAndSlot: async (userId: string, trainingSlotId: string) => {
    await api.delete('/default-assignments', {
      data: { userId, trainingSlotId },
    });
  },

  cancelMyAssignment: async (seriesId: string) => {
    await api.delete(`/default-assignments/me/${seriesId}`);
  },

  getMyAssignmentsDetailed: async () => {
    const response = await api.get<Array<{
      id: string;
      userId: string;
      seriesId: string;
      trainingSlotId: string;
      createdAt: string;
      updatedAt: string;
      series: {
        id: string;
        type: string;
        title: string;
        startDate: string;
        startTime: string;
        endTime?: string;
        recurrencePattern: string;
      };
    }>>('/default-assignments/me');
    return response.data;
  },
};

// Membership API
export type SubscriptionStatus = 'ACTIVE' | 'PAST_DUE' | 'CANCELED' | 'INCOMPLETE' | 'INCOMPLETE_EXPIRED' | 'TRIALING' | 'UNPAID';

export interface MembershipSelectionResponse {
  id: string;
  userId: string;
  stripeSubscriptionId?: string | null;
  currentWeeklyPlan?: MembershipType | null;
  currentITPType?: string | null;
  status?: SubscriptionStatus | null;
  currentPeriodStart?: string | null;
  currentPeriodEnd?: string | null;
  clubFeeIncluded: boolean;
  scheduledWeeklyPlan?: MembershipType | null;
  scheduledITPType?: string | null;
  scheduledEffectiveDate?: string | null;
  createdAt: string;
  updatedAt: string;
  user: {
    id: string;
    email: string;
    name?: string | null;
    surname?: string | null;
    stripeCustomerId?: string | null;
  };
}

export interface CheckoutSessionResponse {
  sessionId: string;
  url: string;
}

export const membershipAPI = {
  getMyMembership: async () => {
    const response = await api.get<MembershipSelectionResponse>('/membership/my-membership');
    return response.data;
  },

  createSubscription: async (membershipType: MembershipType, clubFeeIncluded: boolean = true, itpType?: string, isBasicClubFee?: boolean) => {
    const response = await api.post<CheckoutSessionResponse>('/membership/subscribe', {
      membershipType,
      clubFeeIncluded,
      itpType,
      isBasicClubFee,
    });
    return response.data;
  },

  changePlan: async (membershipType: MembershipType, clubFeeIncluded: boolean = true, itpType?: string) => {
    const response = await api.post<MembershipSelectionResponse>('/membership/change-plan', {
      membershipType,
      clubFeeIncluded,
      itpType,
    });
    return response.data;
  },

  cancelSubscription: async () => {
    const response = await api.post<MembershipSelectionResponse>('/membership/cancel');
    return response.data;
  },

  reactivateSubscription: async () => {
    const response = await api.post<MembershipSelectionResponse>('/membership/reactivate');
    return response.data;
  },
};

// Event Series API
export const eventSeriesAPI = {
  getAll: async () => {
    const response = await api.get<EventSeries[]>('/event-series');
    return response.data;
  },

  create: async (data: Partial<EventSeries> & { date?: string }) => {
    const response = await api.post<EventSeries>('/event-series', data);
    return response.data;
  },

  update: async (id: string, data: Partial<EventSeries> & { date?: string }) => {
    const response = await api.put<EventSeries>(`/event-series/${id}`, data);
    return response.data;
  },

  delete: async (id: string) => {
    const response = await api.delete<{ message: string; cancellation?: CancellationInfo }>(`/event-series/${id}`);
    return response.data;
  },

  getChildren: async (id: string) => {
    const response = await api.get<TrainingSlot[]>(`/event-series/${id}/events`);
    return response.data;
  },
};

// Waitlist API
export const waitlistAPI = {
  joinWaitlist: async (trainingSlotId: string) => {
    const response = await api.post<WaitlistEntry>('/waitlist', { trainingSlotId });
    return response.data;
  },

  leaveWaitlist: async (id: string) => {
    await api.delete(`/waitlist/${id}`);
  },

  getMyWaitlistEntries: async () => {
    const response = await api.get<WaitlistEntry[]>('/waitlist/my-entries');
    return response.data;
  },
};

// Tokens API
export const tokensAPI = {
  getMyTokens: async () => {
    const response = await api.get<TrainingTokenInfo>('/tokens/my-tokens');
    return response.data;
  },
  adminGetBalance: async (userId: string) => {
    const response = await api.get<{ balance: number; tokens: { id: string; issuedAt: string; expiresAt: string }[] }>(`/tokens/admin/balance/${userId}`);
    return response.data;
  },
  adminSetBalance: async (userId: string, count: number) => {
    const response = await api.put<{ userId: string; balance: number }>('/tokens/admin/set', { userId, count });
    return response.data;
  },
};

// Tasks API
export const tasksAPI = {
  getAll: () => api.get<Task[]>('/tasks').then(r => r.data),
  getOne: (id: string) => api.get<Task>(`/tasks/${id}`).then(r => r.data),
  create: (data: Partial<Task>) => api.post<Task>('/tasks', data).then(r => r.data),
  update: (id: string, data: Partial<Task>) => api.put<Task>(`/tasks/${id}`, data).then(r => r.data),
  delete: (id: string) => api.delete(`/tasks/${id}`),
  reorder: (items: { id: string; order: number }[]) => api.put('/tasks/reorder', items),
  getComments: (id: string) => api.get<TaskComment[]>(`/tasks/${id}/comments`).then(r => r.data),
  addComment: (id: string, text: string) => api.post<TaskComment>(`/tasks/${id}/comments`, { text }).then(r => r.data),
  deleteComment: (taskId: string, commentId: string) => api.delete(`/tasks/${taskId}/comments/${commentId}`),
};

// User Notifications API
export const notificationsAPI = {
  getNotifications: async () => {
    const response = await api.get<UserNotification[]>('/notifications');
    return response.data;
  },
  markAllRead: async () => {
    await api.patch('/notifications/read');
  },
  markOneRead: async (id: string) => {
    await api.patch(`/notifications/${id}/read`);
  },
};

// Ideas API
export const ideasAPI = {
  getAll: () => api.get<Idea[]>('/ideas').then(r => r.data),
  create: (text: string) => api.post<Idea>('/ideas', { text }).then(r => r.data),
  toggleLike: (id: string) => api.post<{ liked: boolean; likeCount: number }>(`/ideas/${id}/like`).then(r => r.data),
  delete: (id: string) => api.delete(`/ideas/${id}`),
  getComments: (id: string) => api.get<IdeaComment[]>(`/ideas/${id}/comments`).then(r => r.data),
  addComment: (id: string, text: string) => api.post<IdeaComment>(`/ideas/${id}/comments`, { text }).then(r => r.data),
  deleteComment: (ideaId: string, commentId: string) => api.delete(`/ideas/${ideaId}/comments/${commentId}`),
};

// Assignment Requests API
export const assignmentRequestsAPI = {
  createRequest: async (seriesId: string) => {
    const response = await api.post<AssignmentRequest>('/assignment-requests', { seriesId });
    return response.data;
  },

  createITPRequest: async (itpType: string) => {
    const response = await api.post<AssignmentRequest>('/assignment-requests/itp', { itpType });
    return response.data;
  },

  getMyRequests: async () => {
    const response = await api.get<AssignmentRequest[]>('/assignment-requests/my');
    return response.data;
  },

  getAvailableSeries: async () => {
    const response = await api.get<Array<{
      id: string;
      title: string;
      type: string;
      startTime: string;
      endTime?: string;
      startDate: string;
    }>>('/assignment-requests/available-series');
    return response.data;
  },

  getPending: async () => {
    const response = await api.get<AssignmentRequest[]>('/assignment-requests/pending');
    return response.data;
  },

  approve: async (id: string) => {
    const response = await api.post<{ message: string; assignmentId: string }>(`/assignment-requests/${id}/approve`);
    return response.data;
  },

  reject: async (id: string, adminNote?: string) => {
    const response = await api.post<{ message: string }>(`/assignment-requests/${id}/reject`, { adminNote });
    return response.data;
  },
};

// Tenants API
export const tenantsAPI = {
  getBySlug: (slug: string) => api.get(`/tenants/${slug}`).then(r => r.data),
};

// Health API
export const healthAPI = {
  getHealth: async () => {
    const response = await api.get<{ status: string; version: string }>('/health');
    return response.data;
  },
};

// Test Results API
export interface TestResult {
  id: string;
  userId: string;
  sport: string;
  testType: string;
  resultSeconds: number;
  date: string;
  notes?: string;
  createdAt: string;
}

export interface PercentileData {
  percentile: number;
  totalParticipants: number;
  userResult: number;
  bestResult: number;
  medianResult: number;
}

export const testResultsAPI = {
  create: (data: { sport: string; testType: string; resultSeconds: number; date: string; notes?: string }) =>
    api.post('/test-results', data).then(r => r.data),
  getMyResults: (sport?: string, testType?: string) =>
    api.get('/test-results/my', { params: { sport, testType } }).then(r => r.data as TestResult[]),
  update: (id: string, data: { resultSeconds?: number; date?: string; notes?: string }) =>
    api.put(`/test-results/${id}`, data).then(r => r.data),
  delete: (id: string) =>
    api.delete(`/test-results/${id}`).then(r => r.data),
  getPercentiles: (sport: string, testType: string, gender?: string) =>
    api.get('/test-results/percentiles', { params: { sport, testType, gender } }).then(r => r.data as PercentileData),
  updateGender: (gender: string) =>
    api.put('/test-results/gender', { gender }).then(r => r.data),
  getAllResults: () =>
    api.get('/test-results/all').then(r => r.data as (TestResult & { user: { id: string; name?: string | null; surname?: string | null; email?: string | null; gender?: string | null } })[]),
  adminCreate: (data: { userId: string; sport: string; testType: string; resultSeconds: number; date: string; notes?: string }) =>
    api.post('/test-results/admin', data).then(r => r.data),
  adminUpdate: (id: string, data: { sport?: string; testType?: string; resultSeconds?: number; date?: string; notes?: string }) =>
    api.put(`/test-results/admin/${id}`, data).then(r => r.data),
  adminDelete: (id: string) =>
    api.delete(`/test-results/admin/${id}`).then(r => r.data),
  getTestableUsers: () =>
    api.get('/test-results/users').then(r => r.data as Pick<User, 'id' | 'email' | 'name' | 'surname'>[]),
};

// Gamification API
export const gamificationAPI = {
  getMyPoints: () =>
    api.get('/gamification/me').then(r => r.data),
  getLeaderboard: (limit?: number) =>
    api.get('/gamification/leaderboard', { params: { limit } }).then(r => r.data),
  getStaffLeaderboard: () =>
    api.get('/gamification/staff-leaderboard').then(r => r.data),
  getLevelsConfig: () =>
    api.get('/gamification/levels').then(r => r.data),
  setCompetitionResults: (slotId: string, results: { userId: string; place: number }[]) =>
    api.post('/gamification/competition-results', { slotId, results }).then(r => r.data),
  seasonReset: () =>
    api.post('/gamification/season-reset').then(r => r.data),
  getUserProfile: (userId: string) =>
    api.get(`/gamification/user/${userId}`).then(r => r.data),
};

export default api;
